import React from 'react';
import { useParams } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { readApiError, DEFAULT_ERROR_MESSAGE } from '@/services/api';
import { useDealerConfigOptionQuery } from '../hooks/useDealerConfigOptionQuery';
import { useBulkLinkDealerConfigOptionMutation } from '../hooks/useBulkLinkDealerConfigOptionMutation';
import { DealerConfigOptionWhereUsedPanel } from '../components/DealerConfigOptionWhereUsedPanel';
import { Alert, Badge, TextLink } from '@/shared/components';

export function DealerConfigOptionDetail() {
  const { optionId } = useParams();
  const query = useDealerConfigOptionQuery(optionId);
  const bulkLink = useBulkLinkDealerConfigOptionMutation(optionId);
  const [bulkLinkError, setBulkLinkError] = React.useState<string | null>(null);

  if (!optionId) return <p className="text-mp-error">Invalid config option ID.</p>;
  if (query.isLoading) return <p>Loading config option...</p>;
  if (query.isError || !query.data) {
    return (
      <Alert variant="error">
        <span>Config option not found or failed to load.</span>
      </Alert>
    );
  }

  const option = query.data;

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <TextLink to="/admin/dealers/config-options" color="secondary" className="text-sm">&larr; Back to Config Options</TextLink>
      </div>

      <div>
        <h1 className="text-2xl font-semibold">{option.label ?? option.key_name}</h1>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <code className="text-sm bg-mp-background px-1 rounded">{option.key_name}</code>
          <Badge variant="outline">{option.data_type}</Badge>
          {option.group && (
            <Badge variant="outline">{option.group}</Badge>
          )}
          {option.is_required && (
            <Badge variant="warning">required</Badge>
          )}
        </div>
      </div>

      {option.description
        ? <p className="text-base">{option.description}</p>
        : <p className="italic text-mp-muted">(no description)</p>
      }

      {option.validation_rules && Object.keys(option.validation_rules).length > 0 && (
        <div>
          <h2 className="text-sm font-semibold mb-1">Validation rules</h2>
          <pre className="text-xs bg-mp-background rounded p-2 overflow-x-auto">
            {JSON.stringify(option.validation_rules, null, 2)}
          </pre>
        </div>
      )}

      {bulkLinkError && (
        <Alert variant="error">
          {bulkLinkError}
        </Alert>
      )}

      <DealerConfigOptionWhereUsedPanel
        settingsCount={option.settings_count ?? 0}
        unlinkedCount={option.unlinked_count ?? 0}
        keyName={option.key_name}
        onBulkLink={(opts) => {
          setBulkLinkError(null);
          bulkLink.mutate(opts ?? {}, {
            onError: (err: unknown) => {
              const { message } = readApiError((err as AxiosError).response?.data);
              setBulkLinkError(
                message === DEFAULT_ERROR_MESSAGE ? 'Failed to bulk-link settings' : message,
              );
            },
          });
        }}
        isBulkLinking={bulkLink.isPending}
      />
    </section>
  );
}
